import { Button } from '@/components/ui/button';
import { useExtensionsStore } from '@/stores/extensions';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { AutomationAction } from '@hygge/shared';
import { PRIORITY_OPTIONS, TYPE_OPTIONS } from '@hygge/shared';
import { Plus, X } from 'lucide-react';
import { MemberPicker } from './pickers/member-picker';
import { SprintPicker } from './pickers/sprint-picker';
import { StatusPicker } from './pickers/status-picker';
import { TagPicker } from './pickers/tag-picker';

interface ActionCardProps {
  boardId: string;
  action: AutomationAction;
  onChange: (action: AutomationAction) => void;
  onRemove: () => void;
}

const ACTION_TYPES = [
  { value: 'set_field', label: 'Set field' },
  { value: 'assign', label: 'Assign' },
  { value: 'label', label: 'Add/Remove label' },
  { value: 'move_status', label: 'Move to status' },
  { value: 'add_to_sprint', label: 'Add to sprint' },
  { value: 'comment', label: 'Post comment' },
  { value: 'notify', label: 'Send notification' },
  { value: 'create_subtask', label: 'Create subtask(s)' },
  { value: 'ask_ai', label: 'Ask AI' },
  { value: 'webhook', label: 'Webhook' },
] as const;

type ActionType = (typeof ACTION_TYPES)[number]['value'];

const SETTABLE_FIELDS = [
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'type', label: 'Type' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'storyPoints', label: 'Story Points' },
] as const;

const ASSIGN_MODES = [
  { value: 'person', label: 'Specific person' },
  { value: 'round_robin', label: 'Round robin' },
  { value: 'least_loaded', label: 'Least loaded' },
] as const;

// v1: role audience + email channel are not implemented by the engine, so they
// are not offered (schema rejects them too — see @hygge/shared NotifyAudienceEnum).
const NOTIFY_AUDIENCES = [
  { value: 'assignee', label: 'Assignee' },
  { value: 'reporter', label: 'Reporter' },
  { value: 'person', label: 'Specific person' },
] as const;

const AI_PRESETS = [
  { value: 'triage', label: 'Triage task' },
  { value: 'summarize_comments', label: 'Summarize comments' },
  { value: 'draft_acceptance_criteria', label: 'Draft acceptance criteria' },
  { value: 'custom', label: 'Custom prompt' },
] as const;

function defaultForType(type: ActionType): AutomationAction {
  switch (type) {
    case 'set_field':
      return { type: 'set_field', field: 'priority', value: '' };
    case 'assign':
      return { type: 'assign', mode: 'person' };
    case 'label':
      return { type: 'label', op: 'add', tagId: '' };
    case 'move_status':
      return { type: 'move_status', toStatusId: '' };
    case 'add_to_sprint':
      return { type: 'add_to_sprint', sprint: 'active' };
    case 'comment':
      return { type: 'comment', template: '' };
    case 'notify':
      return { type: 'notify', audience: 'assignee', channels: ['in_app'] };
    case 'create_subtask':
      return { type: 'create_subtask', titles: [''] };
    case 'ask_ai':
      return { type: 'ask_ai', preset: 'triage', output: 'comment' };
    case 'webhook':
      return { type: 'webhook', url: '', includeTask: true };
  }
}

function ActionConfig({
  boardId,
  action,
  onChange,
}: {
  boardId: string;
  action: AutomationAction;
  onChange: (a: AutomationAction) => void;
}) {
  switch (action.type) {
    case 'set_field':
      return (
        <div className="flex gap-2 mt-2">
          <Select
            value={action.field}
            onValueChange={(val) => onChange({ ...action, field: val as typeof action.field })}
          >
            <SelectTrigger className="h-8 text-sm flex-1" aria-label="Field">
              <SelectValue placeholder="Field" />
            </SelectTrigger>
            <SelectContent>
              {SETTABLE_FIELDS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {action.field === 'status' ? (
            <StatusPicker
              boardId={boardId}
              value={(action.value as string) ?? ''}
              onChange={(v) => onChange({ ...action, value: v })}
            />
          ) : action.field === 'assignee' ? (
            <MemberPicker
              boardId={boardId}
              value={action.value as string}
              onChange={(v) => onChange({ ...action, value: v })}
              ariaLabel="Assignee"
            />
          ) : action.field === 'priority' || action.field === 'type' ? (
            <Select
              value={(action.value as string) ?? ''}
              onValueChange={(v) => onChange({ ...action, value: v })}
            >
              <SelectTrigger className="h-9 flex-1" aria-label="Value">
                <SelectValue placeholder="Value" />
              </SelectTrigger>
              <SelectContent>
                {(action.field === 'priority' ? PRIORITY_OPTIONS : TYPE_OPTIONS).map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              type={
                action.field === 'storyPoints'
                  ? 'number'
                  : action.field === 'dueDate'
                    ? 'date'
                    : 'text'
              }
              className="h-9 flex-1"
              placeholder="Value"
              value={action.value != null ? String(action.value) : ''}
              onChange={(e) => onChange({ ...action, value: e.target.value })}
            />
          )}
        </div>
      );

    case 'assign':
      return (
        <div className="flex gap-2 mt-2">
          <Select
            value={action.mode}
            onValueChange={(val) => {
              const mode = val as typeof action.mode;
              if (mode === 'person') {
                onChange({ ...action, mode, assigneeId: action.assigneeId });
              } else {
                onChange({ type: 'assign', mode });
              }
            }}
          >
            <SelectTrigger className="h-8 text-sm flex-1" aria-label="Assign mode">
              <SelectValue placeholder="Mode" />
            </SelectTrigger>
            <SelectContent>
              {ASSIGN_MODES.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {action.mode === 'person' && (
            <MemberPicker
              boardId={boardId}
              value={action.assigneeId}
              onChange={(id) => onChange({ ...action, assigneeId: id })}
              ariaLabel="Assignee"
            />
          )}
        </div>
      );

    case 'label':
      return (
        <div className="flex gap-2 mt-2">
          <Select
            value={action.op}
            onValueChange={(val) => onChange({ ...action, op: val as 'add' | 'remove' })}
          >
            <SelectTrigger className="h-8 text-sm w-28" aria-label="Label operation">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="add">Add</SelectItem>
              <SelectItem value="remove">Remove</SelectItem>
            </SelectContent>
          </Select>
          <TagPicker
            boardId={boardId}
            value={action.tagId}
            onChange={(id) => onChange({ ...action, tagId: id })}
          />
        </div>
      );

    case 'move_status':
      return (
        <div className="mt-2">
          <StatusPicker
            boardId={boardId}
            value={action.toStatusId}
            onChange={(id) => onChange({ ...action, toStatusId: id })}
          />
        </div>
      );

    case 'add_to_sprint':
      return (
        <div className="mt-2">
          <SprintPicker
            boardId={boardId}
            value={action.sprint}
            onChange={(sprint) => onChange({ ...action, sprint })}
          />
        </div>
      );

    case 'comment':
      return (
        <div className="mt-2">
          <Textarea
            className="text-sm min-h-[60px]"
            placeholder="Comment template..."
            value={action.template}
            onChange={(e) => onChange({ ...action, template: e.target.value })}
          />
        </div>
      );

    case 'notify': {
      return (
        <div className="flex flex-col gap-2 mt-2">
          <Select
            value={action.audience}
            onValueChange={(val) => {
              const audience = val as typeof action.audience;
              if (audience === 'person') {
                onChange({ ...action, audience, personId: action.personId });
              } else {
                const { personId: _p, ...rest } = action;
                void _p;
                onChange({ ...rest, audience });
              }
            }}
          >
            <SelectTrigger className="h-8 text-sm" aria-label="Audience">
              <SelectValue placeholder="Audience" />
            </SelectTrigger>
            <SelectContent>
              {NOTIFY_AUDIENCES.map((a) => (
                <SelectItem key={a.value} value={a.value}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {action.audience === 'person' && (
            <MemberPicker
              boardId={boardId}
              value={action.personId}
              onChange={(id) => onChange({ ...action, personId: id })}
              ariaLabel="Recipient"
            />
          )}
          <p className="text-xs text-muted-foreground">Delivered as an in-app notification.</p>
        </div>
      );
    }

    case 'create_subtask': {
      const titles = action.titles;
      return (
        <div className="flex flex-col gap-1.5 mt-2">
          {titles.map((title, i) => (
            <div key={i} className="flex gap-2">
              <Input
                className="h-8 text-sm flex-1"
                placeholder={`Subtask title ${i + 1}`}
                value={title}
                onChange={(e) => {
                  const next = [...titles];
                  next[i] = e.target.value;
                  onChange({ ...action, titles: next });
                }}
              />
              {titles.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() =>
                    onChange({ ...action, titles: titles.filter((_, idx) => idx !== i) })
                  }
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs self-start"
            onClick={() => onChange({ ...action, titles: [...titles, ''] })}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add title
          </Button>
        </div>
      );
    }

    case 'ask_ai':
      return (
        <div className="flex flex-col gap-2 mt-2">
          <div className="flex gap-2">
            <Select
              value={action.preset}
              onValueChange={(val) => onChange({ ...action, preset: val as typeof action.preset })}
            >
              <SelectTrigger className="h-8 text-sm flex-1" aria-label="AI preset">
                <SelectValue placeholder="Preset" />
              </SelectTrigger>
              <SelectContent>
                {AI_PRESETS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={action.output}
              onValueChange={(val) => onChange({ ...action, output: val as 'apply' | 'comment' })}
            >
              <SelectTrigger className="h-8 text-sm flex-1" aria-label="AI output">
                <SelectValue placeholder="Output" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="apply">Apply to task</SelectItem>
                <SelectItem value="comment">Post as comment</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {action.preset === 'custom' && (
            <Textarea
              className="text-sm min-h-[60px]"
              placeholder="Custom AI prompt..."
              value={action.prompt ?? ''}
              onChange={(e) => onChange({ ...action, prompt: e.target.value })}
            />
          )}
        </div>
      );

    case 'webhook':
      return (
        <div className="flex flex-col gap-2 mt-2">
          <Input
            className="h-8 text-sm"
            placeholder="https://your-webhook.com/hook"
            value={action.url}
            onChange={(e) => onChange({ ...action, url: e.target.value })}
          />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch
              checked={action.includeTask}
              onCheckedChange={(checked) => onChange({ ...action, includeTask: checked })}
            />
            Include task payload
          </label>
        </div>
      );
  }
}

export function ActionCard({ boardId, action, onChange, onRemove }: ActionCardProps) {
  const isAssistantEnabled = useExtensionsStore((s) => s.isExtensionEnabled('ai-assistant'));
  // "Ask AI" is the one action type that needs the assistant. Offering it while
  // the extension is off would let someone build a rule whose only action does
  // nothing when it fires. An already-saved ask_ai action still renders — the
  // list only governs what can be picked, and hiding it would silently rewrite
  // somebody's rule to the first entry.
  const selectableActionTypes = ACTION_TYPES.filter(
    (t) => t.value !== 'ask_ai' || isAssistantEnabled || action.type === 'ask_ai',
  );

  return (
    <div className="border border-border rounded-md p-3">
      <div className="flex items-center gap-2">
        <Select
          value={action.type}
          onValueChange={(val) => onChange(defaultForType(val as ActionType))}
        >
          <SelectTrigger className="h-8 text-sm flex-1" aria-label="Action type">
            <SelectValue placeholder="Action type" />
          </SelectTrigger>
          <SelectContent>
            {selectableActionTypes.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onRemove}
          aria-label="Remove action"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <ActionConfig boardId={boardId} action={action} onChange={onChange} />
    </div>
  );
}
